import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllPositions, selectPosition } from '@store/masterData/positionSlice';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ContractShareRequest, ContractShareResponse, ContractSharesClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { Equal, handleError, showNotFoundErrorNotification } from 'src/utility/utils';
import { SharesDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

export interface ContractShare extends ContractShareResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  contractShareList: ContractShare[];
}

const initialState: State = {
  isLoading: true,
  error: false,
  contractShareList: [],
};

const contractShareSlice = createSlice({
  name: 'contractShare',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getContractShareListSuccess: (state, action: PayloadAction<ContractShare[]>) => {
      state.contractShareList = action.payload;
      state.isLoading = false;
    },
    addNewContractShareSuccess: (state, action: PayloadAction<ContractShare>) => {
      const newItem = action.payload;
      state.contractShareList = [newItem, ...state.contractShareList];
      toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
      state.isLoading = false;
    },
    updateContractShareSuccess: (state, action: PayloadAction<ContractShare>) => {
      const updatedItem = action.payload;
      const index = state.contractShareList.findIndex(item => item.contractShareId === updatedItem.contractShareId);
      state.contractShareList = [...state.contractShareList.slice(0, index), updatedItem, ...state.contractShareList.slice(index + 1)];
      toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
      state.isLoading = false;
    },
    deleteContractShareSuccess: (state, action: PayloadAction<number>) => {
      const contractShareId = action.payload;
      state.contractShareList = state.contractShareList.filter(item => item.contractShareId !== contractShareId);
      state.isLoading = false;
    },
  },
});

// Reducer
export default contractShareSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
} = contractShareSlice.actions;

// for useSelector
export const selectContractShare = (state: RootState) => state.contractShare as State;

// ----------------------------------------------------------------------

const removeNullOrEmptyAttributes = (obj) => Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== null && v !== ''));

export function getContractShareList(vesselContractId: number) {
  return async (dispatch, getState) => {
    dispatch(contractShareSlice.actions.startLoading());
    try {
      const client = new ContractSharesClient(undefined, axios);
      const response = await client.getByVesselContractId(vesselContractId);
      dispatch(contractShareSlice.actions.getContractShareListSuccess(response));
      return response;
    } catch (error) {
      dispatch(contractShareSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createContractShare(contractShare: ContractShare) {
  return async (dispatch, getState) => {
    dispatch(contractShareSlice.actions.startLoading());
    try {
      const { contractShareId, ...payload } = removeNullOrEmptyAttributes(contractShare);
      const request = payload as unknown as ContractShareRequest;
      const client = new ContractSharesClient(undefined, axios);
      const response = await client.create(request);
      dispatch(contractShareSlice.actions.addNewContractShareSuccess(response));
    } catch (error) {
      dispatch(contractShareSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createContractShareBatch(vesselContractId: number, sharesData: SharesDataModel[],
  toastId: ReactText, currentProgress: number, totalProgress: number) {
  return async (dispatch, getState) => {
    try {
      await dispatch(getAllPositions());
      const { positionList } = selectPosition(getState());

      const client = new ContractSharesClient(undefined, axios);
      for (const d of sharesData) {
        const positionId = positionList.find(p => Equal(p.positionCode, d.PositionCode))?.positionId;
        if (positionId) {
          const request = {
            vesselContractId,
            positionId,
            share: d.Share,
            costShare: d.CostShare,
            minShare: d.MinShare,
            maxShare: d.MaxShare
          } as ContractShareRequest;
          await client.create(request);
          currentProgress += 1;
          toast.update(toastId, { render: `${stringResources.IMPORT.SHARES_IMPORTING} (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
        } else {
          showNotFoundErrorNotification(d.PositionCode);
        }
      }
    } catch (error) {
      handleError(error);
    }
  };
}

export function updateContractShare(contractShare: ContractShare) {
  return async (dispatch, getState) => {
    dispatch(contractShareSlice.actions.startLoading());
    try {
      const { contractShareId, ...payload } = removeNullOrEmptyAttributes(contractShare);
      const request = payload as unknown as ContractShareRequest;
      const client = new ContractSharesClient(undefined, axios);
      const response = await client.edit(contractShareId as number, request);
      dispatch(contractShareSlice.actions.updateContractShareSuccess(response));
    } catch (error) {
      dispatch(contractShareSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function deleteContractShare(contractShareId: number, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(contractShareSlice.actions.startLoading());
    try {
      const client = new ContractSharesClient(undefined, axios);
      await client.delete(contractShareId);
      dispatch(contractShareSlice.actions.deleteContractShareSuccess(contractShareId));
      if (showNotification) {
        toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
      }
      return true;
    } catch (error) {
      dispatch(contractShareSlice.actions.hasError(true));
      handleError(error);
      return false;
    }
  };
}